import axios from 'axios';

/**
 * RecordFlow has its own backend and its own JWT, so it gets its own axios
 * instance instead of the OCA `fetcher`. Bodies and responses stay snake_case:
 * RecordFlow returns user-defined keys (custom field codes, settings, analysis
 * data) that a deep camelCase conversion would rewrite and fail to round-trip.
 * The bearer token and the session-renewal retry are attached in model/session.
 */
export const rfFetcher = axios.create({
  baseURL:
    process.env.REACT_APP_RECORDFLOW_API_URL ||
    'https://recordflow-api.goavisa.com/api',
});

/**
 * Error thrown for every failed RecordFlow request.
 * `detail` is FastAPI's `detail` payload; `message` is a readable sentence.
 */
export class RecordflowError extends Error {
  constructor(status, detail) {
    super(describeDetail(detail));
    this.status = status;
    this.detail = detail;
    // Babel's Error subclassing breaks `instanceof`; check this flag instead.
    this.isRecordflowError = true;
  }
}

rfFetcher.interceptors.response.use(undefined, async error => {
  if (axios.isCancel(error) || (error && error.isRecordflowError)) {
    throw error;
  }
  const { response } = error;
  let failure;
  if (!response) {
    failure = new RecordflowError(0, 'RecordFlow could not be reached');
  } else {
    let { data } = response;
    // Blob requests (downloads) carry their JSON error inside the blob.
    if (data instanceof Blob) {
      try {
        data = JSON.parse(await data.text());
      } catch (e) {
        data = null;
      }
    }
    const detail = data && data.detail !== undefined ? data.detail : data;
    failure = new RecordflowError(
      response.status,
      detail || response.statusText,
    );
  }
  // Kept so the session can retry the request after renewing the token.
  failure.config = error.config;
  throw failure;
});

function describeDetail(detail) {
  if (typeof detail === 'string') return detail;
  // FastAPI validation errors: [{ loc: [...], msg: '...' }]
  if (Array.isArray(detail)) {
    return detail
      .map(item => (item && item.msg) || String(item))
      .filter(Boolean)
      .join('. ');
  }
  return 'Request failed';
}

const ws = workspaceId => `/workspaces/${workspaceId}`;
const body = request => request.then(response => response.data);

// Auth

export function exchangeSsoToken(token) {
  return body(rfFetcher.post('/auth/sso', { token }));
}

export function fetchMe() {
  return body(rfFetcher.get('/auth/me'));
}

export function updateProfile(data) {
  return body(rfFetcher.patch('/users', data));
}

// Workspaces

export function fetchWorkspaces() {
  return body(rfFetcher.get('/workspaces'));
}

export function createWorkspace(data) {
  return body(rfFetcher.post('/workspaces', data));
}

export function updateWorkspace(workspaceId, data) {
  return body(rfFetcher.patch(ws(workspaceId), data));
}

export function deleteWorkspace(workspaceId) {
  return body(rfFetcher.delete(ws(workspaceId)));
}

export function fetchWorkspaceSettings(workspaceId) {
  return body(rfFetcher.get(`${ws(workspaceId)}/settings`));
}

export function updateWorkspaceSettings(workspaceId, data) {
  return body(rfFetcher.patch(`${ws(workspaceId)}/settings`, data));
}

export function fetchDashboard(workspaceId) {
  return body(rfFetcher.get(`${ws(workspaceId)}/dashboard`));
}

// Categories, projects, currencies, custom fields share one CRUD shape.

function crud(collection) {
  return {
    list: workspaceId =>
      body(rfFetcher.get(`${ws(workspaceId)}/${collection}`)),
    create: (workspaceId, data) =>
      body(rfFetcher.post(`${ws(workspaceId)}/${collection}`, data)),
    update: (workspaceId, id, data) =>
      body(rfFetcher.patch(`${ws(workspaceId)}/${collection}/${id}`, data)),
    remove: (workspaceId, id) =>
      body(rfFetcher.delete(`${ws(workspaceId)}/${collection}/${id}`)),
  };
}

export const categoriesApi = crud('categories');
export const projectsApi = crud('projects');
export const currenciesApi = crud('currencies');
export const customFieldsApi = crud('custom-fields');

// Documents

export function fetchDocuments(workspaceId, { search } = {}) {
  return body(
    rfFetcher.get(`${ws(workspaceId)}/documents`, {
      params: search ? { search } : undefined,
    }),
  );
}

export function fetchDocument(workspaceId, documentId) {
  return body(rfFetcher.get(`${ws(workspaceId)}/documents/${documentId}`));
}

export function uploadDocument(workspaceId, file, config) {
  const form = new FormData();
  form.append('file', file);
  return body(
    rfFetcher.post(`${ws(workspaceId)}/documents/upload`, form, config),
  );
}

export function updateDocument(workspaceId, documentId, data) {
  return body(
    rfFetcher.patch(`${ws(workspaceId)}/documents/${documentId}`, data),
  );
}

function documentAction(action) {
  return (workspaceId, documentId) =>
    body(
      rfFetcher.post(`${ws(workspaceId)}/documents/${documentId}/${action}`),
    );
}

export const analyzeDocument = documentAction('analyze');
export const reviewDocument = documentAction('review');
/** Resolves to the created transaction. */
export const convertDocument = documentAction('convert');
export const archiveDocument = documentAction('archive');

/** Resolves to a Blob of the original file. */
export function downloadDocument(workspaceId, documentId) {
  return body(
    rfFetcher.get(`${ws(workspaceId)}/documents/${documentId}/download`, {
      responseType: 'blob',
    }),
  );
}

// Transactions

export function fetchTransactions(workspaceId, { search } = {}) {
  return body(
    rfFetcher.get(`${ws(workspaceId)}/transactions`, {
      params: search ? { search } : undefined,
    }),
  );
}

export function createTransaction(workspaceId, data) {
  return body(rfFetcher.post(`${ws(workspaceId)}/transactions`, data));
}

export function updateTransaction(workspaceId, transactionId, data) {
  return body(
    rfFetcher.patch(`${ws(workspaceId)}/transactions/${transactionId}`, data),
  );
}

export function deleteTransaction(workspaceId, transactionId) {
  return body(
    rfFetcher.delete(`${ws(workspaceId)}/transactions/${transactionId}`),
  );
}

/** Resolves to `{ imported: number }`. */
export function importTransactionsCsv(workspaceId, file) {
  const form = new FormData();
  form.append('file', file);
  return body(
    rfFetcher.post(`${ws(workspaceId)}/imports/transactions.csv`, form),
  );
}

/** Resolves to a Blob. */
export function exportTransactionsCsv(workspaceId) {
  return body(
    rfFetcher.get(`${ws(workspaceId)}/exports/transactions.csv`, {
      responseType: 'blob',
    }),
  );
}

/** Resolves to a Blob of the whole workspace as a zip. */
export function exportBackup(workspaceId) {
  return body(
    rfFetcher.get(`${ws(workspaceId)}/exports/backup.zip`, {
      responseType: 'blob',
    }),
  );
}

// Invoice generator

export function fetchTemplates(workspaceId) {
  return body(rfFetcher.get(`${ws(workspaceId)}/templates`));
}

export function fetchTemplate(workspaceId, templateId) {
  return body(rfFetcher.get(`${ws(workspaceId)}/templates/${templateId}`));
}

export function createTemplate(workspaceId, payload) {
  const form = new FormData();
  form.append('payload_json', JSON.stringify(payload));
  return body(rfFetcher.post(`${ws(workspaceId)}/templates`, form));
}

export function deleteTemplate(workspaceId, templateId) {
  return body(rfFetcher.delete(`${ws(workspaceId)}/templates/${templateId}`));
}

/** Resolves to a Blob of the invoice PDF. `logo` is an optional File. */
export function generateInvoicePdf(workspaceId, payload, logo) {
  const form = new FormData();
  form.append('payload_json', JSON.stringify(payload));
  if (logo) form.append('image', logo);
  return body(
    rfFetcher.post(`${ws(workspaceId)}/invoices/generate`, form, {
      responseType: 'blob',
    }),
  );
}
